import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { Save, Mail, TestTube2, Building2, Upload, X, Users, Trash2, Shield, User, Key, UserCog, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import UserManagement from '@/components/UserManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings({ token, user, onLogout }) {
  const isPreviewMode = process.env.REACT_APP_PREVIEW_MODE === 'true';
  const isAdmin = user?.role === 'admin' || isPreviewMode;
  
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    use_tls: true
  });
  
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Metaxon AG',
    logo_data: null,
    label_logo_data: null,
    show_watermark: false
  });
  
  const [costSettings, setCostSettings] = useState({
    hourly_rate: 0,
    cost_per_km: 0
  });

  const [packagingSettings, setPackagingSettings] = useState({
    palette_price: 0,
    aufsatzrahmen_price: 0,
    eurobox_price: 0
  });

  const [featureSettings, setFeatureSettings] = useState({
    costs_enabled: true,
    tour_planning_enabled: true,
    packaging_enabled: true,
    qr_label_printing_enabled: true,
    orders_enabled: true
  });
  
  const [logoPreview, setLogoPreview] = useState(null);
  const [labelLogoPreview, setLabelLogoPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // New user management states
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [newPassword, setNewPassword] = useState('');
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    fetchEmailSettings();
    fetchCompanySettings();
    if (isAdmin) {
      fetchUsers();
      fetchCostSettings();
      fetchPackagingSettings();
      fetchFeatureSettings();
    }
  }, []);

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSettings(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Fehler beim Laden der E-Mail-Einstellungen:', error);
      }
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanySettings(response.data);
      if (response.data.logo_data) {
        setLogoPreview(response.data.logo_data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Firmeneinstellungen:', error);
    }
  };

  const fetchCostSettings = async () => {
    try {
      const response = await axios.get(`${API}/cost-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCostSettings({
        hourly_rate: response.data.hourly_rate || 0,
        cost_per_km: response.data.cost_per_km || 0
      });
    } catch (error) {
      if (error.response?.status !== 404 && error.response?.status !== 403) {
        console.error('Fehler beim Laden der Kosteneinstellungen:', error);
      }
    }
  };

  const fetchPackagingSettings = async () => {
    try {
      const response = await axios.get(`${API}/packaging-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackagingSettings({
        palette_price: response.data.palette_price || 0,
        aufsatzrahmen_price: response.data.aufsatzrahmen_price || 0,
        eurobox_price: response.data.eurobox_price || 0
      });
    } catch (error) {
      if (error.response?.status !== 404 && error.response?.status !== 403) {
        console.error('Fehler beim Laden der Verpackungseinstellungen:', error);
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const fetchFeatureSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Feature-Einstellungen:', error);
    }
  };

  const handleSaveFeatureSettings = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(`${API}/settings/features`, featureSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modul-Einstellungen erfolgreich gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${API}/auth/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Benutzer erfolgreich gelöscht');
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/users`, newUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Benutzer erfolgreich angelegt');
      setCreateUserDialogOpen(false);
      setNewUserData({ username: '', email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen des Benutzers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(
        `${API}/auth/users/${selectedUser.id}/password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Passwort erfolgreich geändert');
      setChangePasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/auth/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rolle erfolgreich auf '${newRole}' geändert`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern der Rolle');
    }
  };

  const handleSaveCostSettings = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(`${API}/cost-settings`, costSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kosteneinstellungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePackagingSettings = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(`${API}/packaging-settings`, packagingSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verpackungspreise gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/settings/email`, emailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-Mail-Einstellungen erfolgreich gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySave = async (e) => {
    e.preventDefault();
    
    if (user?.role !== 'admin') {
      toast.error('Nur Administratoren können Firmeneinstellungen ändern');
      return;
    }
    
    setIsLoading(true);

    try {
      await axios.put(`${API}/settings/company`, companySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Firmeneinstellungen erfolgreich gespeichert');
      
      // Reload page to update logo in layout
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 3MB)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo-Datei ist zu groß. Maximale Größe: 3MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bild-Datei');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setCompanySettings({ ...companySettings, logo_data: base64String });
      setLogoPreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setCompanySettings({ ...companySettings, logo_data: null });
    setLogoPreview(null);
  };

  const handleLabelLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo-Datei ist zu groß. Maximale Größe: 3MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setCompanySettings({ ...companySettings, label_logo_data: base64String });
      setLabelLogoPreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLabelLogo = () => {
    setCompanySettings({ ...companySettings, label_logo_data: null });
    setLabelLogoPreview(null);
  };

  const handleTest = async () => {
    setIsTesting(true);

    try {
      await axios.post(
        `${API}/settings/email/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('SMTP-Verbindung erfolgreich getestet!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP-Test fehlgeschlagen');
    } finally {
      setIsTesting(false);
    }
  };

  // Check if user is admin
  if (!isAdmin) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="max-w-4xl mx-auto space-y-6 py-12" data-testid="settings">
          <Card className="glass-effect border-0">
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                <Shield className="h-20 w-20 mx-auto text-red-500 mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Zugriff verweigert</h2>
                <p className="text-gray-600 mb-6">
                  Nur Administratoren haben Zugriff auf die Einstellungen.
                </p>
                <Button onClick={() => window.location.href = '/'} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Zurück zum Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="settings">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Einstellungen</h1>
          <p className="text-gray-600 mt-1">Firmeneinstellungen und E-Mail-Konfiguration</p>
        </div>

        <Tabs defaultValue="company" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="company">
              <Building2 className="mr-2 h-4 w-4" />
              Firma
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="mr-2 h-4 w-4" />
              Benutzer
            </TabsTrigger>
            <TabsTrigger value="modules">
              <UserCog className="mr-2 h-4 w-4" />
              Module
            </TabsTrigger>
            <TabsTrigger value="costs">
              <span className="mr-2">💰</span>
              Fahrtkosten
            </TabsTrigger>
            <TabsTrigger value="packaging">
              <span className="mr-2">📦</span>
              Verpackung
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="mr-2 h-4 w-4" />
              E-Mail
            </TabsTrigger>
          </TabsList>

          {/* Company Settings Tab */}
          <TabsContent value="company">
            <form onSubmit={handleCompanySave} className="space-y-6">
              <Card className="glass-effect border-0">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Firmeneinstellungen</CardTitle>
                      <CardDescription>Firmennamen und Logo anpassen</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Firmenname *</Label>
                    <Input
                      id="company-name"
                      data-testid="company-name-input"
                      type="text"
                      placeholder="z.B. Metaxon AG"
                      value={companySettings.company_name}
                      onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                      required
                    />
                    <p className="text-xs text-gray-500">
                      Dieser Name wird in der Kopfzeile und auf den Lieferscheinen angezeigt
                    </p>
                  </div>

                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label>Firmen-Logo</Label>
                    
                    {logoPreview ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 border-2 border-dashed rounded-lg bg-gray-50">
                          <div className="flex items-center space-x-4">
                            <img 
                              src={logoPreview} 
                              alt="Logo Vorschau" 
                              className="h-16 w-auto object-contain"
                            />
                            <div>
                              <p className="text-sm font-medium">Logo hochgeladen</p>
                              <p className="text-xs text-gray-500">Wird in der Kopfzeile angezeigt</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveLogo}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Entfernen
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                        <Label htmlFor="logo-upload" className="cursor-pointer">
                          <span className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                            Logo hochladen
                          </span>
                          <span className="text-sm text-gray-500"> oder Datei hierher ziehen</span>
                        </Label>
                        <Input
                          id="logo-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          PNG, JPG oder SVG (max. 3MB)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Watermark Option */}
                  {logoPreview && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-0.5">
                          <Label className="text-base font-medium">Logo als Wasserzeichen anzeigen</Label>
                          <p className="text-sm text-gray-500">
                            Zeigt das Logo halbtransparent auf allen Seiten der Anwendung an
                          </p>
                        </div>
                        <Switch
                          checked={companySettings.show_watermark}
                          onCheckedChange={(checked) => 
                            setCompanySettings({...companySettings, show_watermark: checked})
                          }
                        />
                      </div>
                    </div>
                  )}

                  {/* Label Logo Upload */}
                  <div className="space-y-2">
                    <Label htmlFor="label-logo">🏷️ Logo für Etiketten (optional)</Label>
                    <p className="text-sm text-gray-500 mb-2">
                      Separates Logo speziell für Lieferschein-Etiketten. Wird links senkrecht auf dem Aufkleber angezeigt.
                    </p>
                    
                    {labelLogoPreview ? (
                      <div className="space-y-3">
                        <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                          <img 
                            src={labelLogoPreview} 
                            alt="Label Logo Vorschau" 
                            className="max-w-[200px] max-h-[80px] object-contain mx-auto"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleRemoveLabelLogo}
                          className="w-full"
                        >
                          <X className="mr-2 h-4 w-4" />
                          Etikett-Logo entfernen
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                        <Input
                          id="label-logo"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLabelLogoUpload}
                        />
                        <label
                          htmlFor="label-logo"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          <Upload className="h-10 w-10 text-gray-400 mb-2" />
                          <span className="text-sm font-medium text-gray-700 mb-1">
                            Etikett-Logo hochladen
                          </span>
                          <span className="text-xs text-gray-500">
                            PNG, JPG oder SVG (max. 3MB)
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {user?.role !== 'admin' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Nur Administratoren können Firmeneinstellungen ändern
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="metaxon-primary text-white"
                  disabled={isLoading || user?.role !== 'admin'}
                  data-testid="save-company-btn"
                >
                  {isLoading ? (
                    'Wird gespeichert...'
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Speichern
                    </>
                  )}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Users Management Tab */}
          <TabsContent value="users">
            {user?.role !== 'admin' && !user?.can_create_users ? (
              <Card className="glass-effect border-0">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Benutzerverwaltung</CardTitle>
                      <CardDescription>Nur für Administratoren</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <Shield className="h-12 w-12 mx-auto text-yellow-600 mb-2" />
                    <p className="text-sm text-yellow-800">
                      ⚠️ Nur Administratoren können Benutzer verwalten
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <UserManagement token={token} currentUser={user} />
            )}
          </TabsContent>

          {/* Modules/Features Settings Tab */}
          <TabsContent value="modules">
            <form onSubmit={handleSaveFeatureSettings} className="space-y-6">
              <Card className="glass-effect border-0">
                <CardHeader>
                  <CardTitle>Modul-Verwaltung</CardTitle>
                  <CardDescription>
                    Aktivieren oder deaktivieren Sie einzelne Module der Anwendung
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">💰 Kostenberechnung</Label>
                        <p className="text-sm text-gray-500">
                          Fahrtkosten und Zeiterfassung für Touren
                        </p>
                      </div>
                      <Switch
                        checked={featureSettings.costs_enabled}
                        onCheckedChange={(checked) => 
                          setFeatureSettings({...featureSettings, costs_enabled: checked})
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">🗺️ Tourplanung</Label>
                        <p className="text-sm text-gray-500">
                          Kilometer und Zeiterfassung pro Kunde
                        </p>
                      </div>
                      <Switch
                        checked={featureSettings.tour_planning_enabled}
                        onCheckedChange={(checked) => 
                          setFeatureSettings({...featureSettings, tour_planning_enabled: checked})
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">📦 Verpackungsverwaltung</Label>
                        <p className="text-sm text-gray-500">
                          Paletten, Rahmen und Euroboxen Tracking
                        </p>
                      </div>
                      <Switch
                        checked={featureSettings.packaging_enabled}
                        onCheckedChange={(checked) => 
                          setFeatureSettings({...featureSettings, packaging_enabled: checked})
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">🏷️ QR-Code Etikettendruck</Label>
                        <p className="text-sm text-gray-500">
                          QR-Code-Etiketten für DYMO-Drucker erstellen
                        </p>
                      </div>
                      <Switch
                        checked={featureSettings.qr_label_printing_enabled}
                        onCheckedChange={(checked) => 
                          setFeatureSettings({...featureSettings, qr_label_printing_enabled: checked})
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">📦 Bestellungen & Lieferanten</Label>
                        <p className="text-sm text-gray-500">
                          Bestellungen an Lieferanten mit Unterschrift
                        </p>
                      </div>
                      <Switch
                        checked={featureSettings.orders_enabled}
                        onCheckedChange={(checked) => 
                          setFeatureSettings({...featureSettings, orders_enabled: checked})
                        }
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Hinweis:</strong> Deaktivierte Module werden in der Anwendung ausgeblendet.
                    </p>
                  </div>

                  <Button type="submit" disabled={isLoading} className="w-full">
                    {isLoading ? 'Wird gespeichert...' : 'Einstellungen speichern'}
                  </Button>
                </CardContent>
              </Card>
            </form>
          </TabsContent>

          {/* Cost Settings Tab */}
          <TabsContent value="costs">
            {isAdmin ? (
              <form onSubmit={handleSaveCostSettings} className="space-y-6">
                <Card className="glass-effect border-0">
                  <CardHeader>
                    <CardTitle>Kosteneinstellungen für Tourplanung</CardTitle>
                    <CardDescription>
                      Stundenlohn und Kilometerkosten für die Berechnung von Fahrtkosten
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="hourly-rate">Stundenlohn (€/Stunde)</Label>
                      <Input
                        id="hourly-rate"
                        type="number"
                        step="0.01"
                        min="0"
                        value={costSettings.hourly_rate}
                        onChange={(e) => setCostSettings({ ...costSettings, hourly_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="z.B. 25.00"
                      />
                      <p className="text-xs text-gray-500">
                        Stundenlohn des Fahrers in Euro
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cost-per-km">Kosten pro Kilometer (Cent/km)</Label>
                      <Input
                        id="cost-per-km"
                        type="number"
                        step="0.01"
                        min="0"
                        value={costSettings.cost_per_km}
                        onChange={(e) => setCostSettings({ ...costSettings, cost_per_km: parseFloat(e.target.value) || 0 })}
                        placeholder="z.B. 35"
                      />
                      <p className="text-xs text-gray-500">
                        Fahrzeugkosten pro Kilometer in Cent
                      </p>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold mb-2 text-blue-900">Berechnungsbeispiel:</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p>• Stundenlohn: {costSettings.hourly_rate.toFixed(2)} €/Stunde</p>
                        <p>• Kilometersatz: {costSettings.cost_per_km.toFixed(2)} Cent/km</p>
                        <p className="mt-3 font-semibold">Beispiel: 50 km + 30 Min.:</p>
                        <p>• Fahrt: 50 km × {(costSettings.cost_per_km / 100).toFixed(2)} € = {(50 * costSettings.cost_per_km / 100).toFixed(2)} €</p>
                        <p>• Zeit: 0.5 h × {costSettings.hourly_rate.toFixed(2)} € = {(0.5 * costSettings.hourly_rate).toFixed(2)} €</p>
                        <p className="font-bold border-t border-blue-300 pt-1 mt-1">
                          Gesamt: {(50 * costSettings.cost_per_km / 100 + 0.5 * costSettings.hourly_rate).toFixed(2)} €
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-gradient-to-r from-blue-500 to-blue-600"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {isLoading ? 'Speichert...' : 'Einstellungen speichern'}
                  </Button>
                </div>
              </form>
            ) : (
              <Card className="glass-effect border-0">
                <CardContent className="pt-6 text-center text-gray-600">
                  Nur Administratoren haben Zugriff auf Kosteneinstellungen
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Packaging Settings Tab */}
          <TabsContent value="packaging">
            {isAdmin ? (
              <form onSubmit={handleSavePackagingSettings} className="space-y-6">
                <Card className="glass-effect border-0">
                  <CardHeader>
                    <CardTitle>Verpackungsmaterial-Preise</CardTitle>
                    <CardDescription>
                      Preise für die Abrechnung von Paletten, Aufsatzrahmen und Euroboxen
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="palette-price">Preis pro Palette (€)</Label>
                      <Input
                        id="palette-price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={packagingSettings.palette_price}
                        onChange={(e) => setPackagingSettings({ ...packagingSettings, palette_price: parseFloat(e.target.value) || 0 })}
                        placeholder="z.B. 25.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="aufsatzrahmen-price">Preis pro Aufsatzrahmen (€)</Label>
                      <Input
                        id="aufsatzrahmen-price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={packagingSettings.aufsatzrahmen_price}
                        onChange={(e) => setPackagingSettings({ ...packagingSettings, aufsatzrahmen_price: parseFloat(e.target.value) || 0 })}
                        placeholder="z.B. 15.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="eurobox-price">Preis pro Eurobox (€)</Label>
                      <Input
                        id="eurobox-price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={packagingSettings.eurobox_price}
                        onChange={(e) => setPackagingSettings({ ...packagingSettings, eurobox_price: parseFloat(e.target.value) || 0 })}
                        placeholder="z.B. 8.00"
                      />
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold mb-2 text-blue-900">Aktuelle Preise:</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p>• Palette: {packagingSettings.palette_price.toFixed(2)} €</p>
                        <p>• Aufsatzrahmen: {packagingSettings.aufsatzrahmen_price.toFixed(2)} €</p>
                        <p>• Eurobox: {packagingSettings.eurobox_price.toFixed(2)} €</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-gradient-to-r from-blue-500 to-blue-600"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {isLoading ? 'Speichert...' : 'Preise speichern'}
                  </Button>
                </div>
              </form>
            ) : (
              <Card className="glass-effect border-0">
                <CardContent className="pt-6 text-center text-gray-600">
                  Nur Administratoren haben Zugriff auf Verpackungspreise
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Email Settings Tab */}
          <TabsContent value="email">
            <form onSubmit={handleEmailSave} className="space-y-6">
              <Card className="glass-effect border-0">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>SMTP-Server Einstellungen</CardTitle>
                      <CardDescription>Konfigurieren Sie Ihren E-Mail-Server für den Versand</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp-server">SMTP-Server *</Label>
                      <Input
                        id="smtp-server"
                        data-testid="smtp-server-input"
                        type="text"
                        placeholder="smtp.example.com"
                        value={emailSettings.smtp_server}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_server: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-port">SMTP-Port *</Label>
                      <Input
                        id="smtp-port"
                        data-testid="smtp-port-input"
                        type="number"
                        placeholder="587"
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtp-username">SMTP-Benutzername *</Label>
                    <Input
                      id="smtp-username"
                      data-testid="smtp-username-input"
                      type="text"
                      placeholder="ihr-benutzername"
                      value={emailSettings.smtp_username}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtp-password">SMTP-Passwort *</Label>
                    <Input
                      id="smtp-password"
                      data-testid="smtp-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={emailSettings.smtp_password}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="from-email">Absender E-Mail-Adresse *</Label>
                    <Input
                      id="from-email"
                      data-testid="from-email-input"
                      type="email"
                      placeholder="noreply@metaxon.com"
                      value={emailSettings.from_email}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="use-tls">TLS verwenden</Label>
                      <p className="text-sm text-gray-500">Sichere Verbindung aktivieren (empfohlen)</p>
                    </div>
                    <Switch
                      id="use-tls"
                      data-testid="use-tls-switch"
                      checked={emailSettings.use_tls}
                      onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, use_tls: checked })}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleTest}
                  disabled={isTesting || !emailSettings.smtp_server}
                  data-testid="test-smtp-btn"
                >
                  {isTesting ? (
                    'Teste...'
                  ) : (
                    <>
                      <TestTube2 className="mr-2 h-4 w-4" />
                      Verbindung testen
                    </>
                  )}
                </Button>
                <Button
                  type="submit"
                  className="flex-1 metaxon-primary text-white"
                  disabled={isLoading}
                  data-testid="save-settings-btn"
                >
                  {isLoading ? (
                    'Wird gespeichert...'
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Speichern
                    </>
                  )}
                </Button>
              </div>

              <Card className="glass-effect border-0 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Hinweise zur SMTP-Konfiguration</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2 text-gray-700">
                  <p>• <strong>Gmail:</strong> smtp.gmail.com, Port 587, App-spezifisches Passwort verwenden</p>
                  <p>• <strong>Outlook/Office365:</strong> smtp.office365.com, Port 587</p>
                  <p>• <strong>1&1/IONOS:</strong> smtp.ionos.de, Port 587</p>
                  <p>• <strong>Strato:</strong> smtp.strato.de, Port 465 (ohne TLS) oder 587 (mit TLS)</p>
                  <p className="pt-2 text-xs text-gray-600">
                    Nach dem Unterschreiben eines Lieferscheins wird automatisch eine E-Mail mit dem PDF an den Kunden versendet.
                  </p>
                </CardContent>
              </Card>
            </form>
          </TabsContent>
        </Tabs>

        {/* Delete User Confirmation Dialog */}
        <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Benutzer wirklich löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie den Benutzer "{userToDelete?.username}" wirklich löschen? 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700"
              >
                Benutzer löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
